/**
 * The person's look, the pure half (2026-10-26): the vocabulary, the defaults, the zoom steps and
 * the glow's curve. Kept apart from `theme.ts`, whose stores write to <html> the moment they are
 * imported, so these can be tested without a document. `theme.ts` re-exports all of it; read that
 * file's section on the person's look for what the four settings are and who owns them.
 */
export type Surface = 'solid' | 'glass';

/** The prototype's steps. A step list rather than a free number: 117 % is nobody's preference. */
export const ZOOM_LEVELS = [80, 90, 100, 110, 125, 150, 175, 200] as const;
export const ZOOM_MIN = 80;
export const ZOOM_MAX = 200;

/*
 * GLASS OPENS AS THE APPROVED MOCK (coordinator's check, 2026-10-26: "make Glass's default glow
 * the mock's look, so the first switch to Glass shows what Hamzah approved"). Full glow is the
 * mock's own glow, and full strength the mock's see-through cards (as far as the floors allow:
 * 55 % opaque in dark, against the mock's 40 %). The dials only ever take it toward Solid from
 * there. Solid itself is untouched by both: nothing reads them outside the glass blocks.
 */
export const APPEARANCE_DEFAULTS: Readonly<{ surface: Surface; glow: number; strength: number; zoom: number }> = {
  surface: 'solid',
  glow: 100,
  strength: 100,
  zoom: 100,
};

export function clampPct(n: number, lo = 0, hi = 100): number {
  return Number.isFinite(n) ? Math.round(Math.min(hi, Math.max(lo, n))) : lo;
}

/**
 * THE GLOW SLIDER IS PERCEPTUAL, NOT LINEAR. 100 % is the approved mock's glow (`tokens.css`'s
 * `--glow-*`); a linear map left the lower half of the slider all but invisible, because a faint
 * tint halved is nothing on screen. The square root keeps 100 % at the mock and lets 20 % still
 * read as a glow (45 % of it). The CSS receives the factor; the person sees the slider's number.
 */
export function glowFactor(pct: number): number {
  return Math.sqrt(clampPct(pct) / 100);
}

/** The next zoom step in `dir`, or the same level at either end. */
export function stepZoom(current: number, dir: 1 | -1): number {
  if (dir > 0) return ZOOM_LEVELS.find((l) => l > current) ?? current;
  return [...ZOOM_LEVELS].reverse().find((l) => l < current) ?? current;
}
